import useEmblaCarousel from "embla-carousel-react"
import moment from "moment"
import styled from "styled-components"
import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "ui/base/Button/EmblaButtons"
import { mediaSizes } from "ui/theme"

const StarIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M9 1.5L11.09 6.26L16.27 6.88L12.54 10.34L13.59 15.44L9 12.77L4.41 15.44L5.46 10.34L1.73 6.88L6.91 6.26L9 1.5Z'
      fill='#F5A623'
      stroke='#F5A623'
      strokeWidth='0.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export const ClassReview = ({ reviews, rate, reviewCount }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start" })

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  return (
    <Embla>
      <Header>
        <TitleBlock>
          <EmblaTitle>Testimonials</EmblaTitle>
          {(rate || reviewCount) && (
            <RatingSummary>
              <StarsRow>
                {Array.from({ length: 5 }).map((_, i) => (
                  <StarIcon key={i} />
                ))}
              </StarsRow>
              <RatingValue>{rate}</RatingValue>
              <Separator>·</Separator>
              <ReviewCountText>{reviewCount} reviews</ReviewCountText>
            </RatingSummary>
          )}
        </TitleBlock>
        <NavButtons>
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </NavButtons>
      </Header>

      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {reviews.map((item, index) => {
            const name =
              item?.user_data?.first_name ||
              item?.external_user?.first_name ||
              ""
            const date = item?.created_at
              ? moment(item.created_at).format("MMMM D, YYYY")
              : ""
            const rating = item.rating || 0

            return (
              <EmblaSlide key={index}>
                <Card>
                  <CardHeader>
                    <ReviewerInfo>
                      <ReviewerName>{name}</ReviewerName>
                      {date && <ReviewDate>{date}</ReviewDate>}
                    </ReviewerInfo>
                    <CardStars>
                      {Array.from({ length: rating }).map((_, i) => (
                        <StarIcon key={i} />
                      ))}
                    </CardStars>
                  </CardHeader>
                  <ReviewText>{item.review}</ReviewText>
                </Card>
              </EmblaSlide>
            )
          })}
        </EmblaContainer>
      </EmblaViewport>
    </Embla>
  )
}

const Embla = styled.section`
  display: grid;
  max-width: 100%;
  margin: auto;
  gap: 1.2rem;
  border-top: 1px solid rgba(29, 29, 31, 0.1);
  padding-block: 40px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EmblaTitle = styled.h2`
  font-family: Poppins, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;

  @media (width >= ${mediaSizes.md}) {
    font-size: 28px;
  }
  @media (width >= ${mediaSizes.lg}) {
    font-size: 32px;
  }
`

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const StarsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const RatingValue = styled.span`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
`

const Separator = styled.span`
  color: rgba(29, 29, 31, 0.35);
  font-size: 14px;
`

const ReviewCountText = styled.span`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(29, 29, 31, 0.6);
`

const NavButtons = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  flex-shrink: 0;
`

const EmblaViewport = styled.div`
  overflow: hidden;
`

const EmblaContainer = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(1rem * -1);
`

const EmblaSlide = styled.div`
  flex: 0 0 300px;
  min-width: 0;
  padding-left: 1rem;

  @media (width >= ${mediaSizes.md}) {
    flex: 0 0 50%;
  }

  @media (width >= ${mediaSizes.lg}) {
    flex: 0 0 45%;
  }
`

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(29, 29, 31, 0.08);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
  margin-bottom: 0.5rem;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`

const ReviewerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ReviewerName = styled.span`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
`

const ReviewDate = styled.span`
  font-family: Poppins, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(29, 29, 31, 0.5);
`

const CardStars = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`

const ReviewText = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(29, 29, 31, 0.75);
  line-height: 1.65;
  margin: 0;
`
